import Phaser from 'phaser';

const STATE_CRUISE = 0;
const STATE_DIVE = 1;
const STATE_RETURN = 2;

/**
 * UFO that drifts at high altitude, periodically swooping low so the player
 * has to time a jump to bring their bullet height into range. Two hits to kill,
 * touches dino = damage.
 */
export class Ufo extends Phaser.Physics.Arcade.Sprite {
  hp = 2;
  /** Y to sit at while cruising (high — out of horizontal-bullet range from ground) */
  private cruiseY: number;
  /** Y to dive to (mid-air — reachable from a jump apex) */
  private diveY: number;
  private state = STATE_CRUISE;
  private stateChangeAt = 0;
  private nextDiveAt = 0;
  private dead = false;
  /** time accumulator for sine bob */
  private bobT = 0;

  constructor(scene: Phaser.Scene, x: number, cruiseY: number, diveY: number, tint?: number) {
    super(scene, x, cruiseY, 'ufo');
    this.cruiseY = cruiseY;
    this.diveY = diveY;
    scene.add.existing(this);
    scene.physics.add.existing(this);
    this.setOrigin(0.5, 0.5);
    if (tint !== undefined) this.setTint(tint);

    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setAllowGravity(false);
    body.setSize(16, 8);
    body.setOffset(1, 1);
    body.setVelocityX(-50);

    this.nextDiveAt = scene.time.now + Phaser.Math.Between(2500, 5000);
  }

  isDead() {
    return this.dead;
  }

  takeDamage(amount: number, fromX: number) {
    if (this.dead) return;
    this.hp -= amount;
    if (this.hp <= 0) {
      this.dead = true;
      const body = this.body as Phaser.Physics.Arcade.Body;
      body.checkCollision.none = true;
      const dir = this.x < fromX ? -1 : 1;
      this.setVelocity(dir * 80, 220);
      this.scene.tweens.add({
        targets: this,
        angle: 360,
        alpha: 0,
        duration: 700,
        onComplete: () => this.destroy(),
      });
    } else {
      this.scene.tweens.add({
        targets: this,
        alpha: { from: 0.2, to: 1 },
        duration: 70,
        yoyo: true,
        repeat: 2,
      });
    }
  }

  /** Bullet hit — instant kill. */
  killByBullet(fromX: number) {
    if (this.dead) return;
    this.hp = 0;
    this.takeDamage(99, fromX);
  }

  preUpdate(t: number, dt: number) {
    super.preUpdate(t, dt);
    if (this.dead) return;
    this.bobT += dt;

    const now = this.scene.time.now;
    const body = this.body as Phaser.Physics.Arcade.Body;

    switch (this.state) {
      case STATE_CRUISE: {
        // Sinusoidal bob, plus mild forward drift.
        const targetY = this.cruiseY + Math.sin(this.bobT / 220) * 4;
        // Move smoothly toward targetY.
        const dy = targetY - this.y;
        body.setVelocityY(dy * 4);
        if (now >= this.nextDiveAt) {
          this.state = STATE_DIVE;
          this.stateChangeAt = now;
        }
        break;
      }
      case STATE_DIVE: {
        // Accelerate downward toward diveY
        const dy = this.diveY - this.y;
        body.setVelocityY(Phaser.Math.Clamp(dy * 5, -250, 250));
        // Continue forward
        if (Math.abs(dy) < 4 || now - this.stateChangeAt > 1200) {
          this.state = STATE_RETURN;
          this.stateChangeAt = now;
        }
        break;
      }
      case STATE_RETURN: {
        const dy = this.cruiseY - this.y;
        body.setVelocityY(Phaser.Math.Clamp(dy * 4, -180, 180));
        if (Math.abs(dy) < 4 || now - this.stateChangeAt > 1500) {
          this.state = STATE_CRUISE;
          this.nextDiveAt = now + Phaser.Math.Between(2500, 5000);
        }
        break;
      }
    }
  }
}
